import PageWithTabs from "components/PageWithTabs";
import { OrderInfoTab } from "./Tabs/OrderInfoTab";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import SubProceduresTab from "./Tabs/SubProceduresTab";
import AdditionalAttachmentsTab from "./Tabs/AdditionalAttachmentsTab";
import ProgramMinutesTab from "./Tabs/ProgramMinutesTab";
import SpeechesTab from "./Tabs/SpeechesTab";
import { useParams } from "react-router-dom";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useCookieContext } from "contexts";
import SendOrderToFundingUnitModal from "./OrderTaskActionModals/SendOrderToFundingUnitModal";
import ReturnOrderToReviewerModal from "./OrderTaskActionModals/ReturnOrderToReviewerModal";
import PageHeaders, { ReceiptModalProps } from "./PageHeaders";
import PageActions from "./PageActions";
import useOrderView from "./useOrderView";
import ResearcherCloseRequestPage from "./ResearcherCloseRequestPage";
import { EvaluateProgramUpdateRef } from "./Tabs/OrderInfoTab/components/Updates/useProgramOrderUpdateActions";
import { ApproveByCenterManagerSubmitHandlerRef } from "./PageActions/useCloseRequestActions";
import AboutProgramTab from "views/home/programs/ViewProgram/tabs/AboutProgramTab";
import { myWorkStatusClassNames } from "../../researcherOrders/MyUnsignedWork/MyUnsignedWorkGridView";
import { OrderStatus } from "../../../../shared/lookups";
import Modal from "components/blocks/Modal";
import AttachmentCard from "components/blocks/AttachmentCard";
import { ResearcherPaymentLocalResult } from "services/researcherPayments/models/ResearcherPaymentsType";
import { useQuery } from "hooks/useQuery";
import { OrdersService } from "services/orders";

export type FormTabs = "programMinutesTab" | "additionalAttachmentsTab" | "speechesTab";
export type TapsIds = "orderInfoTab" | "subProceduresTab" | FormTabs | "aboutProgram";

export const tabsIds: {
  [key in TapsIds]: TapsIds;
} = {
  orderInfoTab: "orderInfoTab",
  programMinutesTab: "programMinutesTab",
  additionalAttachmentsTab: "additionalAttachmentsTab",
  subProceduresTab: "subProceduresTab",
  speechesTab: "speechesTab",
  aboutProgram: "aboutProgram",
};

export const OrderActivePages = {
  Default: "Default",
  ResearchCloseRequest: "ResearchCloseRequest",
} as const;

export const OrderUserTypes = {
  ResearchCenterManager: "ResearchCenterManager",
  Admin: "Admin",
  FundingUnit: "FundingUnit",
  Researcher: "Researcher",
  DeanOfScientificResearch: "DeanOfScientificResearch",
  ResearchCenterManagerForOrderUpdate: "ResearchCenterManagerForOrderUpdate",
  ResearchCenterManagerForResearcherExtendRequest: "ResearchCenterManagerForResearcherExtendRequest",
  ResearchCenterManagerForResearcherApologiesRequest: "ResearchCenterManagerForResearcherApologiesRequest",
  ResearchCenterManagerForResearcherUpdateRequest: "ResearchCenterManagerForResearcherUpdateRequest",
  ResearcherEditProgramOrderTask: "ResearcherEditProgramOrderTask",
  DeanOfScientificResearchForResearcherApologiesRequest: "DeanOfScientificResearchForResearcherApologiesRequest",
  DeanOfScientificResearchForResearcherExtendRequest: "DeanOfScientificResearchForResearcherExtendRequest",
  ResearcherSendUpdateResponseAfterReturnRequest: "ResearcherSendUpdateResponseAfterReturnRequest",
  ResearcherSendApologizeResponseAfterReturnRequest: "ResearcherSendApologizeResponseAfterReturnRequest",
  ResearcherSendExtendResponseAfterReturnRequest: "ResearcherSendExtendResponseAfterReturnRequest",
  ResearchCenterManagerQueryRequest: "ResearchCenterManagerQueryRequest",
  ResearcherSendQueryResponse: "ResearcherSendQueryResponse",
  GenericWorkFlowActions: "GenericWorkFlowActions",
  //! Close Request
  ResearchCenterManagerForCloseRequest: "ResearchCenterManagerForCloseRequest",
  FundingUnitForCloseRequest: "FundingUnitForCloseRequest",
  VPofDeanForCloseRequest: "VPofDeanForCloseRequest",
  FundFinanceForCloseRequest: "FundFinanceForCloseRequest",
  DeanOfScientificResearchForCloseRequest: "DeanOfScientificResearchForCloseRequest",
  FinanceDepartmentForCloseRequest: "FinanceDepartmentForCloseRequest",
  ResearcherSendCloseResponseAfterReturnRequest: "ResearcherSendCloseResponseAfterReturnRequest",
  //! DeanSecretary
  SecretaryOfTheInitiative: "SecretaryOfTheInitiative",
  ManagerOfTheInitiative: "ManagerOfTheInitiative",
  PayrollOfficer: "PayrollOfficer",
  UnsignedResearcher: "UnsignedResearcher",
} as const;

const OrderView = ({
  userType,
  withGenericActions,
}: {
  userType: keyof typeof OrderUserTypes;
  withGenericActions?: boolean;
}) => {
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { t } = useTranslation(["Orders", "Shared"]);
  const { serialGID, orderId } = useParams();
  const { isAr } = useCookieContext();
  const programUpdateHandlersRef = useRef<EvaluateProgramUpdateRef>(null);
  const approveByCenterManagerSubmitHandlerRef = useRef<ApproveByCenterManagerSubmitHandlerRef>(null);

  const { loading, openedTask, order, programOrderTaskState, orderProgram, setTriggerOrderUpdate } = useOrderView({
    serialGID,
    userType,
    orderId: Number(orderId),
  });

  // Check if sections have data for GenericWorkFlowActions
  const isGenericWorkflow = withGenericActions && userType === OrderUserTypes.GenericWorkFlowActions;

  const { data: programMinutesData } = useQuery({
    queryFn: async () => {
      return await OrdersService.getAllProgramMinutesByProgramOrderId(order?.id ?? 0);
    },
    triggers: [order?.id, isAr],
    options: { enabled: !!order?.id && isGenericWorkflow },
  });

  const { data: speechesData } = useQuery({
    queryFn: async () => {
      return await OrdersService.getAllProgramSpeechesByProgramOrderId(order?.id ?? 0);
    },
    triggers: [order?.id, isAr],
    options: { enabled: !!order?.id && isGenericWorkflow },
  });

  const { data: additionalAttachmentsData } = useQuery({
    queryFn: async () => {
      return await OrdersService.getAllAdditionalAttachmentsByProgramOrderId(order?.id ?? 0);
    },
    triggers: [order?.id],
    options: { enabled: !!order?.id && isGenericWorkflow },
  });

  const { data: subProceduresData } = useQuery({
    queryFn: async () => {
      return await OrdersService.getOrderActions(order?.id ?? 0);
    },
    triggers: [order?.id, isAr],
    options: { enabled: !!order?.id && isGenericWorkflow },
  });

  // Helper function to check if section has data
  const hasProgramMinutesData = isGenericWorkflow ? (programMinutesData?.result?.length ?? 0) > 0 : true;
  const hasSpeechesData = isGenericWorkflow ? (speechesData?.result?.length ?? 0) > 0 : true;
  const hasAdditionalAttachmentsData = isGenericWorkflow ? (additionalAttachmentsData?.result?.length ?? 0) > 0 : true;
  const hasSubProceduresData = isGenericWorkflow ? (subProceduresData?.length ?? 0) > 0 : true;

  const [activePage, setActivePage] = useState<keyof typeof OrderActivePages>(
    userType !== OrderUserTypes.ResearcherSendCloseResponseAfterReturnRequest ? "Default" : "ResearchCloseRequest",
  );
  const [showModalProps, setShowModalProps] = useState<ReceiptModalProps>({
    isOpen: false,
    type: "Receipt",
    data: [] as any,
  });
  const [modalProps, setModalProps] = useState<{
    orderId?: number;
    isOpen: boolean;
    type?: "return" | "respond"; // TODO: 2 types of return - check figma before and after sending to reviewers
  }>({
    orderId: undefined,
    isOpen: false,
    type: undefined,
  });

  const tapsFormRefs = useRef<{
    [key in FormTabs]: React.RefObject<HTMLFormElement>;
  }>({
    additionalAttachmentsTab: useRef<HTMLFormElement>(null),
    programMinutesTab: useRef<HTMLFormElement>(null),
    speechesTab: useRef<HTMLFormElement>(null),
  });

  const onSave = (selectedTabId: TapsIds) => {
    const form = tapsFormRefs.current![selectedTabId as FormTabs]?.current;
    if (form) {
      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      form.classList.add("was-validated");
    }
  };

  const onActionClicked = (action: "return" | "respond") => {
    setModalProps({ isOpen: true, type: action, orderId: programOrderTaskState!.orderId });
  };

  return (
    <>
      <PageWithTabs
        title={
          programOrderTaskState ? (
            orderProgram?.programDetails.find((x) => x.locale === (isAr ? "Ar" : "En"))?.displayName
          ) : (
            <>
              {t("order")} <span dir="ltr">{order?.serialNumber ?? ""}</span>
            </>
          )
        }
        subTitle={
          programOrderTaskState
            ? `${t("programNumber")}: ${orderProgram?.serialNumber ?? ""}`
            : `${t("appliedOn")}: ${toSystemThemeDateFormat(order?.createdOn)}`
        }
        defaultTabId={tabsIds.orderInfoTab}
        loading={loading}
        pageHeaders={PageHeaders({
          order: order,
          programOrderTaskState,
          setShowModalProps,
        })}
        wrapperClassName={activePage === OrderActivePages.Default ? "" : "d-none"}
        actions={(selectedTabId) => (
          <>
            <div className={"d-flex align-items-center"}>
              <span
                className={`badge rounded-4 ${
                  myWorkStatusClassNames[order?.orderStatusId ?? OrderStatus.Inprogress] as string
                } py-2`}
              >
                {order?.orderStatus}
              </span>
            </div>
            <PageActions
              order={order}
              onActionClicked={onActionClicked}
              openedTask={openedTask}
              onSave={onSave}
              programOrderTaskState={programOrderTaskState}
              selectedTabId={selectedTabId}
              userType={userType}
              programOrderId={programOrderTaskState?.orderId ?? 0}
              serialGuid={serialGID ?? ""}
              programUpdateHandlersRef={programUpdateHandlersRef}
              approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
              program={orderProgram}
              setTriggerOrderUpdate={setTriggerOrderUpdate}
              withGenericActions={withGenericActions}
            />
          </>
        )}
        tabs={[
          {
            id: tabsIds.orderInfoTab,
            title: t("orderInfo"),
            content: (
              <OrderInfoTab
                order={order}
                orderProgram={orderProgram}
                userType={userType}
                activePage={activePage}
                setActivePage={setActivePage}
                openedTask={openedTask}
                programUpdateHandlersRef={programUpdateHandlersRef}
                approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
                setTriggerOrderUpdate={setTriggerOrderUpdate}
                withGenericActions={withGenericActions}
              />
            ),
          },
          ...(userType.includes("Researcher")
            ? [
                {
                  id: tabsIds.aboutProgram,
                  title: t("aboutProgram"),
                  content: orderProgram && <AboutProgramTab program={orderProgram} />,
                },
              ]
            : [
                ...(hasProgramMinutesData
                  ? [
                      {
                        id: tabsIds.programMinutesTab,
                        title: t("programMinutes"),
                        content: (
                          <ProgramMinutesTab
                            ref={tapsFormRefs.current[tabsIds.programMinutesTab as FormTabs]}
                            programOrderId={order?.id ?? 0}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(hasSpeechesData
                  ? [
                      {
                        id: tabsIds.speechesTab,
                        title: t("speeches"),
                        content: (
                          <SpeechesTab
                            ref={tapsFormRefs.current[tabsIds.speechesTab as FormTabs]}
                            programOrderId={order?.id ?? 0}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(hasAdditionalAttachmentsData
                  ? [
                      {
                        id: tabsIds.additionalAttachmentsTab,
                        title: t("additionalAttachments"),
                        content: (
                          <AdditionalAttachmentsTab
                            programOrderId={order?.id ?? 0}
                            ref={tapsFormRefs.current[tabsIds.additionalAttachmentsTab as FormTabs]}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(hasSubProceduresData
                  ? [
                      {
                        id: tabsIds.subProceduresTab,
                        title: t("subProcedures"),
                        content: <SubProceduresTab orderId={order?.id ?? 0} />,
                      },
                    ]
                  : []),
              ]),
        ]}
      />

      <ReturnOrderToReviewerModal
        isOpen={modalProps.isOpen && modalProps.type === "return"}
        isInspectMode={order?.isInspectMode}
        onClose={() => {
          setModalProps({ isOpen: false });
        }}
        orderId={modalProps.orderId ?? 0}
        activityGuid={openedTask?.activityGuid}
        serialGuid={serialGID}
      />

      <SendOrderToFundingUnitModal
        isOpen={modalProps.isOpen && modalProps.type === "respond"}
        onClose={() => {
          setModalProps({ isOpen: false });
        }}
        orderId={modalProps.orderId ?? 0}
        serialGuid={serialGID}
        activityGuid={openedTask?.activityGuid}
        userType={userType}
      />

      {activePage === OrderActivePages.ResearchCloseRequest && (
        <ResearcherCloseRequestPage
          userType={userType}
          activePage={activePage}
          order={order}
          orderProgram={orderProgram}
          setActivePage={setActivePage}
          openedTask={openedTask}
          evaluateProgramUpdateRef={programUpdateHandlersRef}
          serialGuid={serialGID ?? ""}
          approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
        />
      )}

      <Modal
        containerClassName="bg-white pb-2"
        isOpen={showModalProps.isOpen}
        onClose={() => {
          setShowModalProps({ isOpen: false, type: "Contract", data: [] as any });
        }}
        title={t("Common:attachments")}
        withHeaderBorder
        size="lg"
        titleClassName="fs-6 text-dark"
      >
        {showModalProps.type === "Contract" ? (
          <div
            dangerouslySetInnerHTML={{
              __html:
                showModalProps.data?.pdfTemplateDetails?.find(
                  (pdf) => pdf.locale?.toLowerCase() == (isAr ? "ar" : "en"),
                )?.templateContent ?? "",
            }}
          />
        ) : (
          <div className="d-flex gap-2 flex-wrap">
            {showModalProps.data.length
              ? showModalProps.data.map((file: ResearcherPaymentLocalResult) => (
                  <AttachmentCard
                    key={file.receipt?.id}
                    attachment={{
                      extension: file.receipt?.extension ?? "",
                      fileName: file.receipt?.name ?? "",
                      id: file.receipt?.id ?? "",
                      size: file.receipt?.size ?? "",
                    }}
                  />
                ))
              : t("allowanceReward")}
          </div>
        )}
      </Modal>
    </>
  );
};

export default OrderView;
